import React, { useMemo, useState, useCallback } from 'react';
import {
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { Settings, Check } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import toast from 'react-hot-toast';

import PageTemplate from 'src/components/PageTemplate';
import { useExpertManagement } from 'src/hooks/useExpertManagement';
import { PATHS } from 'src/constants/config';
import { useReportsData } from 'src/hooks/useReportsData';
import { useQueryFiltering } from 'src/hooks/useQueryFiltering';
import { getExternalEffortsForCompany } from 'src/utils/reportUtils';
import useUsers from 'src/hooks/useUsers';
import useCompanyData from 'src/hooks/useCompanyData';
import { PR_TEAM_IDS } from 'src/constants/prTeam';

import MonthPicker from './components/MonthPicker';
import { getWinsDashboardColumns } from 'src/utils/collections/dashboard';
import { countQueriesByStatus, countExternalEffortsByStatus } from 'src/utils/dashboardUtils';
import {
  submittedStatuses,
  successfulStatuses,
  externalEffortSubmittedStatuses,
  externalEffortSuccessfulStatuses,
} from 'src/utils/collections/status';

const WinsDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { experts, companiesWithActiveExperts } = useExpertManagement();
  const { users } = useUsers();
  const {
    companyData,
    getCompanyData,
    getCompanyQuotas,
    getCompanyPackage,
    getPrimaryLeadId,
    getCompanyStatus,
    updateCompanyData,
    removeCompanyData,
    restoreCompanyData,
    isCompanyHidden,
    normalizeCompanyName,
    isLoading: isCompanyDataLoading,
  } = useCompanyData();

  // State management - simple month selection (YYYY-MM format)
  const [selectedMonth, setSelectedMonth] = useState(moment().format('YYYY-MM'));

  // Filter by primary lead
  const [leadFilter, setLeadFilter] = useState('');

  // Manage (edit) mode — shows remove/restore column and includes hidden companies
  const [isEditMode, setIsEditMode] = useState(false);

  // Confirmation dialog state for removing a company
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [companyToRemove, setCompanyToRemove] = useState(null);

  // Get PR team members for the lead filter dropdown
  const prTeamUsers = useMemo(() => {
    if (!users) return [];
    return users.filter((user) => PR_TEAM_IDS.includes(user.id));
  }, [users]);

  // Calculate start and end dates from selected month
  const startDate = moment(selectedMonth, 'YYYY-MM').startOf('month').format('YYYY-MM-DD');
  const endDate = moment(selectedMonth, 'YYYY-MM').endOf('month').format('YYYY-MM-DD');

  // Custom hooks - pass dates to enable server-side filtering
  const { allQueries, allReports, isLoading } = useReportsData(startDate, endDate);

  // Create a simple date range object for filtering
  const dateRange = useMemo(
    () => ({
      start: moment(startDate),
      end: moment(endDate),
    }),
    [startDate, endDate]
  );

  // Create dateRanges object for useQueryFiltering compatibility
  const dateRanges = useMemo(() => ({ selected: dateRange }), [dateRange]);

  const { filteredQueriesByCompany } = useQueryFiltering(allQueries, experts, companiesWithActiveExperts, 'selected', dateRanges, '');

  const handleViewReport = useCallback(
    (company) => {
      const formattedCompany = company.trim().toLowerCase().replaceAll(' ', '-');
      navigate(`${PATHS.WINS_DASHBOARD.PROFILE.value.replace(':companyID', formattedCompany)}/?startDate=${startDate}&endDate=${endDate}`);
    },
    [navigate, startDate, endDate]
  );

  // Prepare rows for DataGrid - merge companies from config with companies from queries
  const rows = useMemo(() => {
    // Build a map of all companies: from queries + from config
    const allCompanies = new Map();

    // First, add companies with queries (skip hidden companies unless in edit mode)
    Object.entries(filteredQueriesByCompany).forEach(([company, data]) => {
      const trimmedCompany = company.trim();
      const hidden = isCompanyHidden(trimmedCompany);
      if (hidden && !isEditMode) return;
      const companyInfo = companiesWithActiveExperts[company];
      if (data.isUnassigned || companyInfo?.hasActiveExperts) {
        allCompanies.set(trimmedCompany, { hasQueries: true, data, isUnassigned: data.isUnassigned, isHidden: hidden });
      }
    });

    // Then, add companies from config that don't have queries (skip hidden companies unless in edit mode)
    Object.keys(companyData).forEach((normalizedKey) => {
      const hidden = companyData[normalizedKey]?.hidden === true;
      if (hidden && !isEditMode) return;

      // Convert normalized key back to display name (replace hyphens with spaces, capitalize)
      const displayName = normalizedKey
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      // Check if this company already exists (case-insensitive comparison)
      const existingKey = Array.from(allCompanies.keys()).find((key) => normalizeCompanyName(key) === normalizedKey);

      if (!existingKey) {
        allCompanies.set(displayName, { hasQueries: false, data: null, isUnassigned: false, isHidden: hidden });
      }
    });

    // Build rows from all companies
    return Array.from(allCompanies.entries())
      .map(([company, { hasQueries, data, isUnassigned, isHidden }]) => {
        let successfulTotal = 0;
        let submittedTotal = 0;

        if (hasQueries && data) {
          const externalEfforts = getExternalEffortsForCompany(allReports, company, startDate, endDate);

          // Count queries from API
          const querySuccessfulCount = countQueriesByStatus(data.queriesByExpert, successfulStatuses);
          const querySubmittedCount = countQueriesByStatus(data.queriesByExpert, submittedStatuses);

          // Count external efforts
          const externalSuccessfulCount = countExternalEffortsByStatus(externalEfforts, externalEffortSuccessfulStatuses);
          const externalSubmittedCount = countExternalEffortsByStatus(externalEfforts, externalEffortSubmittedStatuses);

          successfulTotal = querySuccessfulCount + externalSuccessfulCount;
          submittedTotal = querySubmittedCount + externalSubmittedCount;
        }

        const quotas = getCompanyQuotas(company);
        const packageType = getCompanyPackage(company);
        const leadId = getPrimaryLeadId(company);

        return {
          id: company,
          package: packageType || '',
          company,
          leadId,
          primaryLead: leadId || '',
          successful: successfulTotal,
          successfulQuota: quotas?.successful || null,
          submitted: submittedTotal,
          submittedQuota: quotas?.submitted || null,
          status: getCompanyStatus(company) || '',
          isUnassigned,
          isHidden,
        };
      })
      .filter((row) => {
        // Filter by selected lead
        if (leadFilter && row.leadId !== leadFilter) return false;
        return true;
      })
      .sort((a, b) => a.company.localeCompare(b.company, undefined, { sensitivity: 'base' }));
  }, [
    filteredQueriesByCompany,
    companiesWithActiveExperts,
    companyData,
    allReports,
    startDate,
    endDate,
    getCompanyQuotas,
    getCompanyPackage,
    getPrimaryLeadId,
    getCompanyStatus,
    normalizeCompanyName,
    isCompanyHidden,
    leadFilter,
    isEditMode,
  ]);

  // Handle individual cell changes — persist to backend immediately
  const handleCellChange = useCallback(
    async (companyName, field, value) => {
      try {
        const companyKey = normalizeCompanyName(companyName);
        const existingData = getCompanyData(companyName) || {};

        let updatedData;
        if (field === 'successfulQuota' || field === 'submittedQuota') {
          const quotaKey = field === 'successfulQuota' ? 'successful' : 'submitted';
          updatedData = {
            ...existingData,
            quotas: { ...existingData.quotas, [quotaKey]: value },
          };
        } else if (field === 'leadId') {
          updatedData = { ...existingData, leadId: value || null };
        } else {
          updatedData = { ...existingData, [field]: value || null };
        }

        await updateCompanyData(companyKey, updatedData);
      } catch {
        toast.error('Failed to save changes');
      }
    },
    [normalizeCompanyName, getCompanyData, updateCompanyData]
  );

  // Handle requesting removal of a company (opens confirmation dialog)
  const handleRemoveCompany = useCallback((row) => {
    setCompanyToRemove(row);
    setRemoveDialogOpen(true);
  }, []);

  // Handle restoring a previously hidden company
  const handleRestoreCompany = useCallback(
    async (row) => {
      try {
        const companyKey = normalizeCompanyName(row.company);
        await restoreCompanyData(companyKey);
        toast.success(`Restored ${row.company}`);
      } catch {
        toast.error('Failed to restore company');
      }
    },
    [normalizeCompanyName, restoreCompanyData]
  );

  // Handle confirming hide of a company
  const handleConfirmRemove = useCallback(async () => {
    if (!companyToRemove) return;
    try {
      const companyKey = normalizeCompanyName(companyToRemove.company);
      await removeCompanyData(companyKey);
      toast.success(`Hid ${companyToRemove.company} from the dashboard`);
    } catch {
      toast.error('Failed to hide company');
    } finally {
      setRemoveDialogOpen(false);
      setCompanyToRemove(null);
    }
  }, [companyToRemove, normalizeCompanyName, removeCompanyData]);

  // Get columns with the view report handler
  const columns = useMemo(
    () =>
      getWinsDashboardColumns(handleViewReport, {
        users,
        prTeamUsers,
        onCellChange: handleCellChange,
        onRemoveCompany: handleRemoveCompany,
        onRestoreCompany: handleRestoreCompany,
        isEditMode,
      }),
    [handleViewReport, users, prTeamUsers, handleCellChange, handleRemoveCompany, handleRestoreCompany, isEditMode]
  );

  return (
    <PageTemplate loading={isLoading || isCompanyDataLoading} loadingMessage="Loading takes about 15-30 seconds">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              flex: 1,
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              letterSpacing: '-0.02em',
            }}
          >
            Dashboard
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: { xs: 'center', md: 'flex-end' } }}>
            <Button
              variant="contained"
              size="small"
              color={isEditMode ? 'success' : 'primary'}
              startIcon={isEditMode ? <Check /> : <Settings />}
              onClick={() => setIsEditMode((v) => !v)}
            >
              {isEditMode ? 'Done' : 'Manage'}
            </Button>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select value={leadFilter} onChange={(e) => setLeadFilter(e.target.value)} displayEmpty sx={{ bgcolor: 'white' }}>
                <MenuItem value="">
                  <em>All Leads</em>
                </MenuItem>
                {prTeamUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.firstName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
          </Box>
        </Box>

        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            hideFooter
            getRowClassName={(params) => (params.row.isHidden ? 'row--hidden' : '')}
            initialState={{
              sorting: {
                sortModel: [{ field: 'company', sort: 'asc' }],
              },
            }}
            sx={{
              '& .row--hidden': {
                opacity: 0.55,
                fontStyle: 'italic',
              },
              borderColor: 'divider',
              backgroundColor: 'white',
              boxShadow: '0 0 10px rgba(0,0,0,0.1)',
              '& .MuiDataGrid-cell': {
                borderRight: 1,
                borderBottom: 1,
                borderColor: 'divider',
                px: 2,
                overflow: 'visible !important',
                whiteSpace: 'nowrap',
              },
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: 2,
                borderColor: 'divider',
                backgroundColor: 'white',
                '& .MuiDataGrid-columnHeader': {
                  py: 2,
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                  },
                },
              },
              '& .MuiDataGrid-columnHeader': {
                borderRight: 1,
                borderColor: 'divider',
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          />
        </Box>
      </Box>

      {/* Hide Company Confirmation Dialog */}
      <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)}>
        <DialogTitle>Hide Company</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Hide <strong>{companyToRemove?.company}</strong> from the dashboard? Its saved settings (package, quotas, lead) are kept — you can show it
            again anytime from Manage mode.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmRemove} variant="contained">
            Hide
          </Button>
        </DialogActions>
      </Dialog>
    </PageTemplate>
  );
};

export default WinsDashboard;
